import {
  BottomSheetContainer,
  Colors,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  HEADER_HEIGHT,
  HorizontalSpacing,
  IconCheck,
  IconDownload,
  Spinner,
  Tag,
  Typography,
  useSafeArea,
  useScreenSize,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useCallback, useEffect, useMemo } from 'react';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useAnimatedProps,
  useAnimatedRef,
  useSharedValue,
  useAnimatedScrollHandler,
  scrollTo,
  withTiming,
} from 'react-native-reanimated';
import type { FlatList as RNFlatList } from 'react-native';
import { scheduleOnRN } from 'react-native-worklets';
import { useGoodsCouponList } from '../queries/useGoodsCouponList';
import type { GoodsCoupon } from 'shared/src/api';
import { bridge, isAndroid, localizeNumber } from 'shared/src/utils';
import { HTMLText } from 'shared/src/component/HTMLText';
import { useOnPressGoodsCouponRedeemAll } from '../hooks/useOnPressGoodsCouponRedeemAll';
import { usePostRedeemCoupon } from '../../../queries/usePostRedeemCoupon';
import { useLogViewCouponListModal } from '../hooks/useLogViewCouponListModal';
import { useLogDownloadCoupon } from '../hooks/useLogDownloadCoupon';
import { useFeatureFlagValue } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';

const DOCKED_PRIMARY_BUTTON_HEIGHT = 56 + 12 * 2;

const CouponApplicableType = {
  DEFAULT: 1,
  STACKABLE: 2,
  NOT_STACKABLE: 3,
};

type CouponSectionViewItem =
  | { type: 'title'; title: string }
  | { type: 'coupon'; item: GoodsCoupon };

const CouponItemView: React.FC<{
  coupon: GoodsCoupon;
}> = props => {
  const { mutateAsync: postRedeemCoupon, isPending: loading } =
    usePostRedeemCoupon();
  const { data: myInfo } = useMyInfo();

  const logDownloadCoupon = useLogDownloadCoupon();

  const onPressRedeemCoupon = useCallback(async () => {
    if (myInfo?.is_anonymous) {
      bridge.assertLogin({});
      setTimeout(() => {
        bridge.toast('로그인이나 회원가입이 필요해요');
      }, 200);
      return;
    }

    try {
      await postRedeemCoupon({ sno: props.coupon.sno });
      bridge.toast('쿠폰이 발급되었습니다');
      logDownloadCoupon({ coupon: props.coupon, isDownloadAll: false });
    } catch (ex) {
      const error = ex as Error;
      bridge.toast(error.message);
    }
  }, [logDownloadCoupon, myInfo, postRedeemCoupon, props.coupon]);

  return (
    <Flex.Row
      padding={{ horizontal: 24, vertical: 24 }}
      shadowStep={'small'}
      backgroundColor={'white'}
      borderRadius={8}
    >
      <Flex flex={1}>
        <Flex.Row gap={8} alignItems={'center'}>
          <Typography type={'h2'} color={'content_blue_primary'}>
            {props.coupon.discount_type === 'PERCENT'
              ? `${props.coupon.discount_percent}%`
              : `${localizeNumber(props.coupon.discount_price)}원`}
          </Typography>
          {props.coupon.applicable_type ===
            CouponApplicableType.NOT_STACKABLE && (
            <Tag size={'xsmall'} color={'pink'} type={'secondary'}>
              중복 사용 불가
            </Tag>
          )}
        </Flex.Row>
        <VerticalSpacing size={6} />
        <Typography type={'subtitle2'}>{props.coupon.name}</Typography>
        <VerticalSpacing size={6} />
        {props.coupon.conditions_with_markup.split('\n').map(condition => (
          <Flex margin={{ top: 6 }} key={condition}>
            <HTMLText type="body4" color="content_tertiary" numberOfLines={2}>
              {condition}
            </HTMLText>
          </Flex>
        ))}
        <VerticalSpacing size={6} />
      </Flex>
      <HorizontalSpacing size={16} />
      <Flex.Center>
        {props.coupon.is_issued ? (
          <Flex.Center gap={4}>
            <IconCheck fill={Colors['content_tertiary']} size={24} />
            <Typography type={'subtitle6'} color={'content_tertiary'}>
              발급완료
            </Typography>
          </Flex.Center>
        ) : (
          <Flex.Center
            width={52}
            height={52}
            borderRadius={26}
            backgroundColorValue={ColorValues.blue5}
            onPress={onPressRedeemCoupon}
          >
            {loading ? (
              <Spinner color={'content_blue_primary'} size={24} />
            ) : (
              <IconDownload fill={Colors['content_blue_primary']} size={24} />
            )}
          </Flex.Center>
        )}
      </Flex.Center>
    </Flex.Row>
  );
};
const CouponGap = () => {
  return <VerticalSpacing size={16} />;
};

export const useGoodsCouponListBottomSheet = createUseBottomSheetModal(
  ({ cancel }) => {
    const { height: screenHeight } = useScreenSize();
    const { top, bottom } = useSafeArea();
    const { data: coupons, isLoading: loadingCouponList } =
      useGoodsCouponList();
    const enableStackableFlag = useFeatureFlagValue('stackable-coupon');

    const { action: redeemAllCouponAction, loading: loadingRedeemAll } =
      useOnPressGoodsCouponRedeemAll(
        coupons?.allCoupons?.filter(coupon => !coupon.is_issued) ?? [],
      );
    useLogViewCouponListModal(coupons);

    const groupedCouponList = useMemo<CouponSectionViewItem[]>(() => {
      if (!coupons) return [];
      const couponList: CouponSectionViewItem[] = [];
      if (enableStackableFlag) {
        if (coupons.marketCoupons.length > 0) {
          couponList.push({
            title: `중복 사용 가능 쿠폰 ${coupons.marketCoupons.length}장`,
            type: 'title' as const,
          });
          couponList.push(
            ...coupons.marketCoupons
              .filter(coupon => coupon.is_enabled)
              .map(coupon => ({
                type: 'coupon' as const,
                item: coupon,
              })),
          );
        }

        if (coupons.cartCoupons.length > 0) {
          couponList.push({
            title: `장바구니 쿠폰 ${coupons.cartCoupons.length}장`,
            type: 'title' as const,
          });

          couponList.push(
            ...coupons.cartCoupons
              .filter(coupon => coupon.is_enabled)
              .map(coupon => ({
                type: 'coupon' as const,
                item: coupon,
              })),
          );
        }
      } else {
        couponList.push(
          ...coupons.allCoupons
            .filter(coupon => coupon.is_enabled)
            .map(coupon => ({
              type: 'coupon' as const,
              item: coupon,
            })),
        );
      }

      return couponList;
    }, [coupons, enableStackableFlag]);

    const isAllCouponIssued = useMemo(() => {
      if (!coupons) return true;

      return (
        coupons.allCoupons.filter(coupon => !coupon.is_issued).length === 0
      );
    }, [coupons]);

    const contentHeight = useMemo(() => {
      return (
        (screenHeight -
          HEADER_HEIGHT -
          top -
          bottom -
          DOCKED_PRIMARY_BUTTON_HEIGHT) *
        0.86
      );
    }, [bottom, screenHeight, top]);

    const initialHeight =
      contentHeight + HEADER_HEIGHT + DOCKED_PRIMARY_BUTTON_HEIGHT + 12;
    const heightAnim = useSharedValue(0);
    const startHeight = useSharedValue(initialHeight);
    const animatedListRef = useAnimatedRef<RNFlatList<CouponSectionViewItem>>();
    const scrollY = useSharedValue(0);
    const isScrollLocked = useSharedValue(false);

    useEffect(() => {
      heightAnim.value = withTiming(initialHeight, { duration: 300 });
    }, [heightAnim, initialHeight]);

    const animatedStyle = useAnimatedStyle(() => ({
      height: heightAnim.value + bottom,
      overflow: 'hidden' as const,
    }));

    // worklet에서 스크롤 offset 추적
    const scrollHandler = useAnimatedScrollHandler({
      onScroll: event => {
        scrollY.value = event.contentOffset.y;

        // 시트가 드래그 중이면 스크롤을 0으로 강제 잠금
        if (isScrollLocked.value) {
          scrollTo(animatedListRef, 0, 0, false);
        }
      },
    });

    // Android: FlatList 자체 스크롤 비활성, Pan에서 scrollTo로 제어
    // iOS: bounces로 음수 offset 사용 가능하므로 자체 스크롤 허용
    const listAnimatedProps = useAnimatedProps(() => ({
      scrollEnabled: isAndroid ? false : !isScrollLocked.value,
    }));

    const canDragSheet = useSharedValue(false);
    const startScrollY = useSharedValue(0);

    const panGesture = useMemo(
      () =>
        Gesture.Pan()
          .activeOffsetY([-10, 10])
          .onStart(() => {
            // 드래그 시작 시 scrollY로 시트 드래그 가능 여부 결정
            canDragSheet.value = scrollY.value <= 1;
            startScrollY.value = scrollY.value;
            startHeight.value = heightAnim.value;
          })
          .onUpdate(event => {
            // 스크롤 위치가 맨 위가 아니었으면 Pan은 아무것도 안 함 → FlatList만 스크롤
            if (!canDragSheet.value) {
              const scrollAmount = startScrollY.value + -event.translationY;
              if (scrollAmount > 0) {
                scrollTo(animatedListRef, 0, scrollAmount, false);
              }
              canDragSheet.value = scrollAmount <= 1;
              return;
            }

            if (event.translationY > 0) {
              // 아래로 드래그 → 시트 축소
              isScrollLocked.value = true;
              scrollTo(animatedListRef, 0, 0, false);
              const newHeight = startHeight.value - event.translationY;
              heightAnim.value = Math.max(
                0,
                Math.min(initialHeight, newHeight),
              );
            } else if (isScrollLocked.value) {
              // 위로 드래그 → 잠금 해제
              isScrollLocked.value = false;
              heightAnim.value = initialHeight;
            } else {
              // 잠금 해제 후 위로 드래그 계속 → FlatList 스크롤로 전환
              const scrollAmount = startScrollY.value + -event.translationY;
              console.log(scrollAmount);
              if (scrollAmount > 0) {
                scrollTo(animatedListRef, 0, scrollAmount, false);
              }
            }
          })
          .onEnd(event => {
            if (!canDragSheet.value || !isScrollLocked.value) {
              isScrollLocked.value = false;
              // Android: velocity 기반 관성 스크롤
              if (isAndroid) {
                const currentScroll = startScrollY.value + -event.translationY;
                const flingTarget = currentScroll + -event.velocityY * 0.3;
                const clampedTarget = Math.max(0, flingTarget);
                scrollTo(animatedListRef, 0, clampedTarget, true);
              }
              return;
            }
            isScrollLocked.value = false;
            canDragSheet.value = false;
            if (heightAnim.value >= initialHeight * 0.7) {
              heightAnim.value = withTiming(initialHeight, { duration: 200 });
            } else {
              heightAnim.value = withTiming(0, { duration: 200 });
              scheduleOnRN(cancel);
            }
          }),
      [
        cancel,
        heightAnim,
        initialHeight,
        startHeight,
        scrollY,
        isScrollLocked,
        canDragSheet,
        startScrollY,
        animatedListRef,
      ],
    );

    const gesture = useMemo(
      () =>
        Gesture.Simultaneous(
          panGesture,
          Gesture.Native().shouldCancelWhenOutside(false),
        ),
      [panGesture],
    );

    return (
      <GestureDetector gesture={gesture}>
        <Animated.View style={animatedStyle}>
          <BottomSheetContainer
            title={'쿠폰'}
            maxHeight={screenHeight}
            bottomDocked={{
              type: 'normal',
              paired: false,
              color: 'blue',
              stroke: true,
              button: {
                state:
                  loadingCouponList || loadingRedeemAll
                    ? 'loading'
                    : isAllCouponIssued
                      ? 'disabled'
                      : undefined,
                title: isAllCouponIssued ? '다운로드 완료' : '모두 다운받기',
                icon: isAllCouponIssued ? undefined : <IconDownload />,
                outline: true,

                onPress: redeemAllCouponAction,
              },
            }}
            onPressClose={cancel}
          >
            {loadingCouponList ? (
              <Flex.Center height={contentHeight}>
                <Spinner color={'content_blue_primary'} />
              </Flex.Center>
            ) : (
              <Flex height={contentHeight}>
                <Animated.FlatList
                  ref={animatedListRef}
                  style={{ flex: 1 }}
                  contentContainerStyle={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                  }}
                  animatedProps={listAnimatedProps}
                  onScroll={scrollHandler}
                  scrollEventThrottle={16}
                  data={groupedCouponList}
                  renderItem={({ item }) => {
                    if (item.type === 'coupon') {
                      return <CouponItemView coupon={item.item} />;
                    }

                    if (item.type === 'title') {
                      return (
                        <Flex padding={{ top: 20 }}>
                          <Typography
                            type={'subtitle2'}
                            color={'content_secondary'}
                          >
                            {item.title}
                          </Typography>
                        </Flex>
                      );
                    }
                    return null;
                  }}
                  ItemSeparatorComponent={CouponGap}
                />
              </Flex>
            )}
          </BottomSheetContainer>
        </Animated.View>
      </GestureDetector>
    );
  },
);
